<template>
    <div class="bell-wrapper">
        <button type="button" class="layout-topbar-button bell-button" :aria-label="ariaLabel" :title="ariaLabel"
            @click="toggle">
            <i class="pi pi-bell" v-badge.danger="badgeValue || null"></i>
        </button>

        <OverlayPanel ref="panelRef" class="bell-panel" @show="loadNotificaciones">
            <div class="bell-header">
                <span class="font-bold">Notificaciones</span>
                <Button v-if="sinLeer > 0" label="Marcar todas como leídas" class="p-button-text p-button-sm"
                    @click="leerTodas" />
            </div>

            <div v-if="loading" class="p-3 text-center text-500">
                <i class="pi pi-spin pi-spinner"></i>
            </div>
            <div v-else-if="!notificaciones.length" class="p-3 text-center text-500">
                No tienes notificaciones.
            </div>
            <ul v-else class="bell-list">
                <li v-for="n in notificaciones" :key="n.id" class="bell-item" :class="{ 'is-unread': !n.read_at }"
                    @click="abrir(n)">
                    <i :class="['pi', iconFor(n.nivel), `nivel-${n.nivel}`]"></i>
                    <div class="bell-item-body">
                        <div class="bell-item-title">{{ n.titulo }}</div>
                        <div class="bell-item-msg">{{ n.mensaje }}</div>
                        <div class="bell-item-date">{{ formatDate(n.created_at) }}</div>
                    </div>
                </li>
            </ul>

            <div class="bell-footer">
                <Button :label="pendientes > 0 ? `Ver mis pendientes (${pendientes})` : 'Ver mis pendientes'"
                    icon="pi pi-inbox" class="p-button-sm w-full" @click="irAPendientes" />
            </div>
        </OverlayPanel>

        <Dialog v-model:visible="displayIntroModal" :modal="true" :closable="false" :showHeader="false" :style="{width: '500px', maxWidth: '90vw'}" :contentStyle="{ padding: '0', borderRadius: '1rem', overflow: 'hidden' }" @hide="closeIntroModal">
            <div class="relative bg-primary text-white p-5 text-center" style="border-top-left-radius: 1rem; border-top-right-radius: 1rem;">
                <Button icon="pi pi-times" class="p-button-rounded p-button-text absolute" style="top: 0.5rem; right: 0.5rem; color: white" @click="closeIntroModal" />
                <div class="mb-3 inline-flex align-items-center justify-content-center border-circle" style="width: 64px; height: 64px; background: rgba(255,255,255,0.2)">
                    <i class="pi pi-bell text-white" style="font-size: 2rem"></i>
                </div>
                <h2 class="m-0 text-2xl font-bold">¡Novedades en ERPZ!</h2>
            </div>
            
            <div class="p-5">
                <p class="m-0 text-lg text-700 text-center mb-5 line-height-3">
                    Hemos agregado una nueva sección de notificaciones y pendientes para facilitar tu trabajo diario.
                </p>
                <div class="flex flex-column gap-4">
                    <div class="flex align-items-start gap-3">
                        <div class="flex align-items-center justify-content-center bg-blue-100 border-circle p-2 flex-shrink-0" style="width: 44px; height: 44px">
                            <i class="pi pi-bolt text-blue-600 text-xl"></i>
                        </div>
                        <div>
                            <span class="font-bold text-900 block mb-1 text-lg">Alertas en tiempo real</span>
                            <span class="text-600 line-height-3">Recibe avisos inmediatos sobre requisiciones por aprobar y cambios en tus proyectos.</span>
                        </div>
                    </div>
                    <div class="flex align-items-start gap-3">
                        <div class="flex align-items-center justify-content-center bg-green-100 border-circle p-2 flex-shrink-0" style="width: 44px; height: 44px">
                            <i class="pi pi-window-maximize text-green-600 text-xl"></i>
                        </div>
                        <div>
                            <span class="font-bold text-900 block mb-1 text-lg">Acceso directo</span>
                            <span class="text-600 line-height-3">Revisa tus notificaciones recientes sin salir de la página actual.</span>
                        </div>
                    </div>
                    <div class="flex align-items-start gap-3">
                        <div class="flex align-items-center justify-content-center bg-orange-100 border-circle p-2 flex-shrink-0" style="width: 44px; height: 44px">
                            <i class="pi pi-list text-orange-600 text-xl"></i>
                        </div>
                        <div>
                            <span class="font-bold text-900 block mb-1 text-lg">Mis pendientes</span>
                            <span class="text-600 line-height-3">Un botón rápido para ver todas tus tareas consolidadas en un solo lugar.</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="p-4 pt-0 flex justify-content-center pb-5">
                <Button label="¡Entendido, gracias!" icon="pi pi-check" @click="closeIntroModal" autofocus class="p-button-lg w-full sm:w-auto" style="border-radius: 2rem" />
            </div>
        </Dialog>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useApp } from 'src/composables/app.js';
import { useAuth } from 'src/composables/auth.js';
import { ApiService } from 'src/services/api.js';
import { listen } from 'src/services/realtime';

// Respaldo si el tiempo real no está disponible (sin Pusher o sin worker de colas).
const POLL_MS = 60000;

const app = useApp();
const auth = useAuth();
const router = useRouter();

const panelRef = ref(null);
const pendientes = ref(0);
const sinLeer = ref(0);
const notificaciones = ref([]);
const loading = ref(false);
const displayIntroModal = ref(false);

const badgeValue = computed(() => sinLeer.value);
const ariaLabel = computed(() => `Notificaciones: ${sinLeer.value} sin leer, ${pendientes.value} pendientes`);

async function loadCount() {
    if (document.hidden) return;
    try {
        const { data } = await ApiService.get('pendientes/count');
        pendientes.value = data.pendientes ?? 0;
        sinLeer.value = data.notificaciones_sin_leer ?? 0;
    } catch (e) {
        // silencioso: el contador se reintenta en el siguiente ciclo
    }
}

async function loadNotificaciones() {
    loading.value = true;
    try {
        const { data } = await ApiService.get('notificaciones');
        notificaciones.value = data.records ?? [];
        sinLeer.value = data.sin_leer ?? 0;
    } catch (e) {
        app.showPageRequestError(e);
    } finally {
        loading.value = false;
    }
}

function toggle(event) {
    panelRef.value.toggle(event);
}

async function abrir(n) {
    if (!n.read_at) {
        n.read_at = new Date().toISOString();
        sinLeer.value = Math.max(0, sinLeer.value - 1);
        ApiService.post(`notificaciones/leer/${n.id}`, {}).catch(() => {});
    }
    panelRef.value.hide();
    if (n.url) router.push(n.url);
}

async function leerTodas() {
    try {
        await ApiService.post('notificaciones/leer-todas', {});
        const now = new Date().toISOString();
        notificaciones.value.forEach((n) => { n.read_at = n.read_at || now; });
        sinLeer.value = 0;
    } catch (e) {
        app.showPageRequestError(e);
    }
}

function irAPendientes() {
    panelRef.value.hide();
    router.push('/pendientes');
}

function iconFor(nivel) {
    return { success: 'pi-check-circle', danger: 'pi-times-circle', warn: 'pi-exclamation-circle' }[nivel] || 'pi-info-circle';
}

function formatDate(value) {
    if (!value) return '';
    return new Date(value).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

function closeIntroModal() {
    displayIntroModal.value = false;
    if (auth.userId) {
        localStorage.setItem(`erpz_bell_modal_seen_${auth.userId}`, '1');
    }
}

let timer = null;
let stopRealtime = () => {};

function onVisible() {
    if (!document.hidden) loadCount();
}

onMounted(() => {
    if (auth.userId) {
        const modalKey = `erpz_bell_modal_seen_${auth.userId}`;
        if (!localStorage.getItem(modalKey)) {
            displayIntroModal.value = true;
        }
    }

    loadCount();
    timer = setInterval(loadCount, POLL_MS);
    document.addEventListener('visibilitychange', onVisible);

    // Laravel publica las notificaciones en private-user.{id} (ver AuthTable::receivesBroadcastNotificationsOn).
    stopRealtime = listen(`private-user.${auth.userId}`, {
        'erpz.notification': (n) => {
            sinLeer.value += 1;
            app.flashMsg(n.titulo, n.mensaje, n.nivel === 'danger' ? 'error' : (n.nivel || 'info'));
            loadCount();
        },
    });
});

onUnmounted(() => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
    stopRealtime();
});
</script>

<style scoped>
.bell-wrapper {
    display: flex;
    align-items: center;
    margin-right: 0.5rem;
}

.bell-button .pi-bell {
    font-size: 1.25rem;
}

.bell-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--surface-border);
}

.bell-list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 360px;
    overflow-y: auto;
    width: 340px;
    max-width: calc(100vw - 4rem);
}

.bell-item {
    display: flex;
    gap: 0.75rem;
    padding: 0.75rem 0.25rem;
    border-bottom: 1px solid var(--surface-border);
    cursor: pointer;
}

.bell-item:hover {
    background: var(--surface-hover);
}

.bell-item.is-unread .bell-item-title {
    font-weight: 700;
}

.bell-item.is-unread {
    background: var(--highlight-bg, var(--surface-100));
}

.bell-item-body {
    min-width: 0;
}

.bell-item-msg {
    color: var(--text-color-secondary);
    font-size: 0.875rem;
}

.bell-item-date {
    color: var(--text-color-secondary);
    font-size: 0.75rem;
    margin-top: 0.25rem;
}

.nivel-success { color: var(--green-500); }
.nivel-danger { color: var(--red-500); }
.nivel-warn { color: var(--orange-500); }
.nivel-info { color: var(--blue-500); }

.bell-footer {
    padding-top: 0.75rem;
}
</style>
